import { action, makeObservable, observable } from 'mobx';

import Auth from '../services/AuthService';
import userManager from './UserManager';
import lessonManager from './LessonManager';
import studentActivityManager from './StudentActivityManager';
import { LessonMode } from '../../Constants';

// import ContentService from '../services/ContentService';

export class DocReaderManager {
  constructor() {
    makeObservable(this);
  }

  _cwin = null;

  /** 1-based page index; updated on docReader `pagechanged` / `pageCurrent` responses (MobX-observable for PDF Activity UI). */
  @observable currentPageNumber = 1;

  _isPageChanging = false;
  /** True while a lesson-driven `goToPage` / queued `sideNavPdfPage` is in flight (not user PDF toolbar nav). */
  _expectProgrammaticPageChange = false;
  // _currentPageAnnotationValue = null;

  @observable pageAnnotations = new Map();

  @observable pageHighlights = new Map();

  @observable printUrl = null;

  @observable printSvg = null;

  @observable documentSessionId = null;

  /** Lesson element id of the activity whose iframe last called `setDocumentSessionId` (PDF / bookmark container). */
  @observable docReaderFrameOwnerLessonElementId = null;

  /** When set, PdfActivity scrolls the open docReader iframe to this page (1-based); cleared after `goToPage`. */
  @observable sideNavPdfPage = null;

  /** Toggle question list in the flyout when paging (mirror VideoManager.allowShowDividedVideoQuestions). */
  @observable allowShowPdfActivityQuestions = true;

  /** PDF split layout: show empty question panel (e.g. page 1 with no linked child). */
  @observable pdfQuestionPanelBlank = false;

  /** User picked a child from activity nav; hold that question until the PDF page changes. */
  @observable pdfManualChildNav = false;

  /** When set, question panel shows this child instead of currentLessonElementId. */
  @observable pdfDisplayChildLessonElementId = null;

  /**
   * Set from LessonManager.setCurrentSlideIndex when navigating to a PDF activity child (`childPages`).
   * Applied immediately via applyLessonDirectedPdfNavigation (goToPage or sideNavPdfPage if iframe not ready).
   */
  @observable lessonDirectedPdfTargetPage = null;

  /**
   * True when the slide index pointed at the PDF activity container (isActivity) and we jumped to the first child.
   * Used by NavMenuService flyout sync for “page 1 has no questions” vs direct child navigation.
   */
  @observable pdfEntryViaActivityParentSlide = false;

  /** PDF activity parent `lessonElementId`; used with `documentSessionId` so we never `goToPage` on a stale iframe. */
  @observable lessonDirectedPdfParentLessonElementId = null;

  /**
   * Footer “next” and goto-menu cards already drive docReader via `goToPage`; skip the extra
   * `syncCurrentPageFromReader` after iframe load (see PdfActivity.initializeDocReaderFrame).
   */
  @observable suppressSyncCurrentPageAfterPdfIframeLoad = false;

  /**
   * Footer / activity “next” targets the PDF via `goToPage`/`sideNav`; skip redundant
   * `scheduleAlignDocReaderToCurrentPdfChild` when `pageloaded` fires (runs after iframe `load`).
   */
  @observable suppressScheduleAlignOnDocReaderPageload = false;

  /**
   * Footer “previous” with `navigateWithinActivity` off uses `previousSlide()`; skip `scheduleAlign…` on
   * `pageloaded` (lesson already aligned via `setCurrentSlideIndex`).
   */
  @observable suppressScheduleAlignOnDocReaderPageloadFromFooterPrevious = false;

  /**
   * 1-based PDF page the current lesson slide expects (`childPages`). While MobX `currentPageNumber`
   * has not caught up after `goToPage`/`sideNav`, flyout sync must not map “reader page → first child
   * on that page” or it jumps away from the card-selected child (summary/goto).
   */
  @observable pdfLessonPendingReaderPage = null;

  /**
   * Goto menu with `navigateWithinActivity` off: lesson shows PDF page 1 while slide may be first child on
   * page 1 or first filtered child in the activity; flyout sync skips `staleReaderPageOne` until resolved.
   */
  @observable pdfGotoOutsideActivityNavPageOne = false;

  /**
   * With `navigateWithinActivity` off, footer Previous: suppress auto-opening the interactive flyout
   * in post-navigation and for one PDF `applyPdfActivityReaderFlyoutSync` pass (often blank). Goto
   * menu uses `fromGotoMenuCard` in `doPostNavigationActions` only so PDF flyout can still open when
   * page 1 has questions.
   */
  @observable suppressInteractiveFlyoutOpenAfterFooterPreviousSlide = false;

  @action loadPageAnnotations(pageHighlightsAnnotations) {
    if (pageHighlightsAnnotations) {
      pageHighlightsAnnotations.forEach((pageHA) => {
        if (pageHA.pageIndex !== null) {
          this.pageAnnotations.set(pageHA.pageIndex, pageHA.annotation);
          this.pageHighlights.set(pageHA.pageIndex, pageHA.highlight);
        }
      });
    }
  }

  /**
   * Satellite / logging session id only. Does **not** mark which iframe owns `_cwin` — call
   * `setDocReaderFrameOwnerLessonElementId` immediately after `initializeDocReaderFrame` binds the window.
   */
  @action setDocumentSessionId(lessonElementId) {
    const activityId = studentActivityManager.activityId;
    const { userId } = userManager;
    if (lessonElementId != null && activityId != null) {
      this.documentSessionId = `${activityId}-${lessonElementId}`;
    } else if (lessonElementId != null && userId) {
      /** Preview has no `activityId`, so key marks to this teacher so they are not shared. */
      this.documentSessionId = `${userId}-${lessonElementId}`;
    } else if (lessonElementId != null) {
      /** Preview / paths without `activityId` still need a stable session stamp for satellites, etc. */
      this.documentSessionId = `doc-${lessonElementId}`;
    }
  }

  /**
   * Must run in the same turn as `setContentWindow` for this activity’s iframe, never on mount alone —
   * otherwise `_contentWindowMatchesPdfActivityParent` thinks the PDF is “live” while `_cwin` is still
   * another activity’s docReader frame.
   */
  @action setDocReaderFrameOwnerLessonElementId = (lessonElementId) => {
    this.docReaderFrameOwnerLessonElementId = lessonElementId ?? null;
  }

  @action setPrintUrl = (url) => {
    if (url !== null) {
      this.printUrl = url;
    }
  }

  @action setPrintSvg = (svg) => {
    if (svg !== null) {
      this.printSvg = svg;
    }
  }

  @action setPageAnnotation = (pageIndex, annotation) => {
    if (pageIndex !== null && annotation !== null) {
      this.pageAnnotations.set(pageIndex, annotation);
    }
  }

  @action setPageHighlight = (pageIndex, highlight) => {
    if (pageIndex !== null && highlight !== null) {
      this.pageHighlights.set(pageIndex, highlight);
    }
  }

  @action setContentWindow = (cWin) => {
    this._cwin = cWin ?? null;
  }

  /** Call when PdfActivity unmounts so we do not postMessage a detached iframe on the next navigation. */
  @action releasePdfActivityDocReaderSession = () => {
    this._cwin = null;
    this.documentSessionId = null;
    this.docReaderFrameOwnerLessonElementId = null;
    this.pdfLessonPendingReaderPage = null;
    this.pdfGotoOutsideActivityNavPageOne = false;
    this.suppressInteractiveFlyoutOpenAfterFooterPreviousSlide = false;
    this._expectProgrammaticPageChange = false;
    this.sideNavPdfPage = null;
    this.clearPdfQuestionPanelOverrides();
  }

  _contentWindowMatchesPdfActivityParent = (pdfParentLessonElementId) => {
    if (!this._cwin || pdfParentLessonElementId == null) {
      return false;
    }
    const owner = this.docReaderFrameOwnerLessonElementId;
    if (owner == null) {
      return false;
    }
    return String(owner) === String(pdfParentLessonElementId);
  }

  /** True when `_cwin` belongs to the PdfActivity for this PDF parent (not a detached/stale iframe). */
  isLivePdfActivityIframe = (pdfParentLessonElementId) => {
    return this._contentWindowMatchesPdfActivityParent(pdfParentLessonElementId);
  }

  @action setCurrentPage = (page) => {
    if (page !== null && page !== undefined) {
      this.currentPageNumber = page;
    }
  }

  /**
   * Query docReader for the real current page (`pageCurrent`). MobX `currentPageNumber` can drift
   * if `pagechanged` doesn’t fire (programmatic goto, iframe focus, or another activity reused the manager).
   */
  syncCurrentPageFromReader = () => {
    if (!this._cwin) {
      return;
    }
    this.dcData_get(this._cwin, {
      pageCurrent: null
    }, (response) => {
      if (response?.pageCurrent != null && response.pageCurrent !== undefined) {
        this.setCurrentPage(response.pageCurrent);
      }
    });
  }

  @action setSideNavPdfPage = (page) => {
    this.sideNavPdfPage = page;
    this._expectProgrammaticPageChange = page != null;
  }

  isExpectingProgrammaticPageChange = () => {
    return this._expectProgrammaticPageChange || this.sideNavPdfPage != null;
  }

  @action setAllowShowPdfActivityQuestions = (allow) => {
    this.allowShowPdfActivityQuestions = allow;
  }

  @action setPdfQuestionPanelBlank = (blank) => {
    this.pdfQuestionPanelBlank = !!blank;
  }

  @action setPdfManualChildNav = (manual) => {
    this.pdfManualChildNav = !!manual;
  }

  @action setPdfDisplayChildLessonElementId = (lessonElementId) => {
    this.pdfDisplayChildLessonElementId = lessonElementId ?? null;
  }

  @action clearPdfQuestionPanelOverrides = () => {
    this.pdfQuestionPanelBlank = false;
    this.pdfManualChildNav = false;
    this.pdfDisplayChildLessonElementId = null;
  }

  @action clearLessonDirectedPdfNavigation = () => {
    this.lessonDirectedPdfTargetPage = null;
    this.pdfEntryViaActivityParentSlide = false;
    this.lessonDirectedPdfParentLessonElementId = null;
    this.pdfLessonPendingReaderPage = null;
  }

  @action clearPdfLessonPendingReaderPage = () => {
    this.pdfLessonPendingReaderPage = null;
  }

  @action setLessonDirectedPdfNavigation = ({
    targetPage,
    entryViaActivityParent,
    pdfParentLessonElementId,
  }) => {
    this.lessonDirectedPdfTargetPage = targetPage != null ? Number(targetPage) : null;
    this.pdfEntryViaActivityParentSlide = !!entryViaActivityParent;
    this.lessonDirectedPdfParentLessonElementId =
      pdfParentLessonElementId != null ? pdfParentLessonElementId : null;
    this.pdfLessonPendingReaderPage =
      targetPage != null ? Number(targetPage) : null;
  }

  @action clearPdfEntryViaActivityParentSlide = () => {
    this.pdfEntryViaActivityParentSlide = false;
  }

  @action clearPdfGotoOutsideActivityNavPageOne = () => {
    this.pdfGotoOutsideActivityNavPageOne = false;
  }

  @action setPdfGotoOutsideActivityNavPageOne = (value) => {
    this.pdfGotoOutsideActivityNavPageOne = !!value;
  }

  @action setSuppressInteractiveFlyoutOpenAfterFooterPreviousSlide = (value) => {
    this.suppressInteractiveFlyoutOpenAfterFooterPreviousSlide = !!value;
  }

  @action clearSuppressInteractiveFlyoutOpenAfterFooterPreviousSlide = () => {
    this.suppressInteractiveFlyoutOpenAfterFooterPreviousSlide = false;
  }

  @action
  consumeSuppressSyncCurrentPageAfterPdfIframeLoad = () => {
    const value = this.suppressSyncCurrentPageAfterPdfIframeLoad;
    this.suppressSyncCurrentPageAfterPdfIframeLoad = false;
    return value;
  }

  @action
  consumeSuppressScheduleAlignOnDocReaderPageload = () => {
    const value = this.suppressScheduleAlignOnDocReaderPageload;
    this.suppressScheduleAlignOnDocReaderPageload = false;
    return value;
  }

  @action setSuppressScheduleAlignOnDocReaderPageloadFromFooterPrevious = (value) => {
    this.suppressScheduleAlignOnDocReaderPageloadFromFooterPrevious = !!value;
  }

  @action clearSuppressScheduleAlignOnDocReaderPageloadFromFooterPrevious = () => {
    this.suppressScheduleAlignOnDocReaderPageloadFromFooterPrevious = false;
  }

  @action
  consumeSuppressScheduleAlignOnDocReaderPageloadFromFooterPrevious = () => {
    const value = this.suppressScheduleAlignOnDocReaderPageloadFromFooterPrevious;
    this.suppressScheduleAlignOnDocReaderPageloadFromFooterPrevious = false;
    return value;
  }

  /**
   * Reset all iframe-load suppression flags before programmatic navigation (`navToSlideClickHandler`,
   * footer Next). Includes footer-previous pageload suppress so stale flags cannot leak across navigations.
   */
  @action clearPdfIframeNavigationSuppressFlags = () => {
    this.suppressSyncCurrentPageAfterPdfIframeLoad = false;
    this.suppressScheduleAlignOnDocReaderPageload = false;
    this.suppressScheduleAlignOnDocReaderPageloadFromFooterPrevious = false;
  };

  /**
   * Lesson navigation onto a PDF slide already applies `goToPage` via `setCurrentSlideIndex`; optionally
   * skip redundant iframe sync (goto/next) and pageload align (next / footer next only).
   */
  @action setSuppressPdfSlideLandingFromLessonNav = ({
    iframeSync = false,
    pageloadAlign = false,
  } = {}) => {
    if (iframeSync) {
      this.suppressSyncCurrentPageAfterPdfIframeLoad = true;
    }
    if (pageloadAlign) {
      this.suppressScheduleAlignOnDocReaderPageload = true;
    }
  };

  /**
   * Scroll docReader to the lesson’s target page after setCurrentSlideIndex (iframe may not exist yet).
   */
  @action
  applyLessonDirectedPdfNavigation = () => {
    const page = this.lessonDirectedPdfTargetPage;
    if (page == null || page < 1) {
      return;
    }
    const nPage = Number(page);
    /**
     * Always queue `goToPage` for the lesson target (including page 1). MobX `currentPageNumber`
     * can be wrong after switching activities or before `pagechanged`; the early-exit that skipped
     * when nPage===1 && nCurrent===1 left the iframe on the wrong page.
     */
    const parentId = this.lessonDirectedPdfParentLessonElementId;
    this._queueLessonPdfPageOrSideNav(nPage, parentId);
    this.lessonDirectedPdfTargetPage = null;
    this.lessonDirectedPdfParentLessonElementId = null;
  }

  _queueLessonPdfPageOrSideNav = (p, pdfParentLessonElementId) => {
    const live = this._contentWindowMatchesPdfActivityParent(pdfParentLessonElementId);
    if (live) {
      this.goToPage(p);
      return;
    }
    /**
     * Stale `_cwin` after PdfActivity unmount left `setContentWindow` non-null; do not postMessage
     * a detached frame — queue the page for the next iframe load instead.
     */
    if (this._cwin) {
      this.setContentWindow(null);
    }
    this.setSideNavPdfPage(p);
  }

  /**
   * ReadSpeaker docReader API: `pageGoto` (general commands). See docs/ReadSpeaker docReader 4.11 JavaScript API v02.pdf
   * Always asks docReader for `pageCurrent` first so we don’t skip `pageGoto` when MobX is stale.
   */
  goToPage(pageNum) {
    if (!this._cwin || pageNum == null || pageNum < 1) {
      return;
    }
    const n = Number(pageNum);
    this.dcData_get(this._cwin, {
      pageCurrent: null
    }, (response) => {
      const actual = response?.pageCurrent != null ? Number(response.pageCurrent) : null;
      if (actual != null && !Number.isNaN(actual)) {
        this.setCurrentPage(actual);
      }
      if (actual === n) {
        this._expectProgrammaticPageChange = false;
        return;
      }
      this._expectProgrammaticPageChange = true;
      this.dcData_get(this._cwin, {
        pageGoto: [pageNum]
      }, (gotoResponse) => {
        if (gotoResponse?.pageGoto === false) {
          console.warn('docReader pageGoto failed for page', pageNum);
          this._expectProgrammaticPageChange = false;
        }
        setTimeout(() => this.syncCurrentPageFromReader(), 200);
      });
    });
  }

  @action setIsPageChanging = (isChanging) => {
    if (isChanging !== null) {
      this._isPageChanging = isChanging;
    }
  }

  @action
  clearHighlightsAnnotations = () => {
    this.pageAnnotations.clear();
    this.pageHighlights.clear();
  }

  canEdit() {
    if (lessonManager.playerMode === LessonMode.ACTIVITY_MODE ||
      lessonManager.playerMode === LessonMode.TEACHER_PRESENTATION ||
      lessonManager.playerMode === LessonMode.PREVIEW) {
      return true;
    }
    return false;
  }

  getAnnotation(key) {
    if (this.pageAnnotations.has(key)) {
      return this.pageAnnotations.get(key);
    }
    return null;
  }

  getHighlight(key) {
    if (this.pageHighlights.has(key)) {
      return this.pageHighlights.get(key);
    }
    return null;
  }

  getContentWindow() {
    return this._cwin;
  }

  /**
   * Annotations are an SVG overlay on the rendered page, so docReader refuses to show the
   * annotation toolbar while the reader is in text mode — the tool silently stays in whatever
   * display mode it had. Switch to layout mode first, then enable editing.
   */
  activateAnnotationTool() {
    const hltDt = {};
    hltDt['highlighter.displayModeSet'] = ['view'];
    this.windowPostMessage(hltDt);

    const annoDt = {};
    annoDt['annotations.displayModeSet'] = ['edit'];

    this.dcData_get(this._cwin, { viewmodeGet: null }, (response) => {
      if (response?.viewmodeGet !== 'textmode') {
        this.windowPostMessage(annoDt);
        return;
      }
      this.dcData_get(this._cwin, { viewmodeSet: ['layoutmode'] }, () => {
        this.windowPostMessage(annoDt);
      });
    });
  }

  activateHighlightTool() {
    const annoDt = {};
    annoDt['annotations.displayModeSet'] = ['view'];
    this.windowPostMessage(annoDt);

    const hltDt = {};
    hltDt['highlighter.displayModeSet'] = ['edit'];
    this.windowPostMessage(hltDt);
  }

  neutralizeBothTools() {
    const annoDt = {};
    annoDt['annotations.displayModeSet'] = ['view'];
    this.windowPostMessage(annoDt);

    const hltDt = {};
    hltDt['highlighter.displayModeSet'] = ['view'];
    this.windowPostMessage(hltDt);
  }

  hideBothTools() {
    const annoDt = {};
    annoDt['annotations.displayModeSet'] = ['hidden'];
    this.windowPostMessage(annoDt);

    const hltDt = {};
    hltDt['highlighter.displayModeSet'] = ['off'];
    this.windowPostMessage(hltDt);
  }

  windowPostMessage(dataObj) {
    const eventId = `eventid-${Math.round(Math.random() * 10000)}`;
    const obj = {
      type: 'dcdata',
      dataId: eventId,
      dataObj
    };
    this._cwin.postMessage(obj, '*');

    // console.log('* windowPostMessage posted');
  }

  async doSaveActions(clear = true) {
    let annoResp = null;
    // save the current page annotation
    this.dcData_get(this._cwin, {
      contentGet: null
    }, async (response) => {
      annoResp = response.contentGet;
      this.dcData_get(this._cwin, {
        'highlighter.contentGet': null
      }, async (response) => {
        const hltResp = response['highlighter.contentGet'];
        await this.addUpdateDocumentHighlightAnnotationPage(null, this.currentPageNumber, annoResp, hltResp, clear);
      });
    });
  }

  async getPrintContentSvg() {
    this.dcData_get(this._cwin, {
      contentGetForPrint: null
    }, async (printableResponse) => {
      const printAnnotationContent = printableResponse.contentGetForPrint;

      if (printableResponse && printAnnotationContent) {
        this.setPrintSvg(printAnnotationContent);
        if (printAnnotationContent) {
          this.dcData_get(this._cwin, {
            contentSet: [printAnnotationContent]
          }, () => {
            // console.log('There was annotation data for this page.');
          });
        } else {
          console.log('There was no printable annotation data for this page.');
        }
        console.log(printAnnotationContent);
      } else {
        console.log('There was no printable content data for this page.');
      }
    });
  }

  async getPrintPageUrl() {
    this.dcData_get(this._cwin, {
      pageImageSrc: null
    }, async (urlResponse) => {
      if (urlResponse && urlResponse.pageImageSrc) {
        this.setPrintUrl(urlResponse.pageImageSrc);
      } else {
        console.log('There was no url data for this page.');
      }
    });
  }

  async doPagePrint() {
    this.dcData_get(this._cwin, {
      pagePrint: null
    }, async (response) => {
      setTimeout(() => {
      // reset to regular annotations.
        const pageAnnotationData = this.getAnnotation(response.pageCurrent);
        if (pageAnnotationData) {
          this.dcData_get(this._cwin, {
            contentSet: [pageAnnotationData]
          }, () => {
          });
        } else {
        }
      }, 5000);
    });
  }

  async doPageChangedActions() {
    // set current page number
    this.dcData_get(this._cwin, {
      pageCurrent: null
    }, async (response) => {
      this.setCurrentPage(response.pageCurrent);
      this._expectProgrammaticPageChange = false;
      const pageAnnotationData = this.getAnnotation(response.pageCurrent);
      if (pageAnnotationData) {
        this.dcData_get(this._cwin, {
          contentSet: [pageAnnotationData]
        }, () => {
          // console.log('There was annotation data for this page.');
        });
      } else {
        // console.log('There was no annotation data for this page.');
      }

      const pageHighlightData = this.getHighlight(response.pageCurrent);
      if (pageHighlightData) {
        this.dcData_get(this._cwin, {
          'highlighter.contentSet': [pageHighlightData]
        }, () => {
          // console.log('There was highlight data for this page.');
        });
      } else {
        // console.log('There was no highlight data for this page.');
      }
    });
  }

  async clearPageAnnotation() {
    this.dcData_get(this.getContentWindow(), {
      contentClear: null
    }, async () => {
      // _content.fetch();
    });
  }

  async clearPageHighlight() {
    this.dcData_get(this.getContentWindow(), {
      'highlighter.contentClear': null
    }, async () => {
      // _content.fetch();
    });
  }

  _relay = (e) => {
    // This is needed since some browsers use the originalEvent property instead of just sending it straight up.
    const message = e.originalEvent || e;

    if (message.data && message.data.type) {
      // We want to check the message type excplicitly, in case there are other applications that use the same message strucuture.
      switch (message.data.type) {
        case 'dcevent':
          this.dcEvent_fire(message.data.params.eventId, message.data.params.args); // Event message
          break;

        case 'dcdata':
          this.dcData_receive(message.data.params.dataId, message.data.params.returnObj); // Data message
          break;
      }
    } else {
      console.warn('Warning! Could not determine the message type. It\'s possible it was not a docReader message.');
    }
  }

  initializeDocReaderFrame = async (id) => {
    // console.log('* Start initializeDocReaderFrame');
    const iFrameId = id || 'dcr-frame';
    this.setContentWindow(document.getElementById(iFrameId).contentWindow);

    if (window.addEventListener) {
      window.addEventListener('message', this._relay, false);
    } else {
      window.attachEvent('onmessage', this._relay);
    }

    this.addEventListeners();
  }

  addEventListeners = async () => {
    this.dcEvent_on(this._cwin, 'pageloaded', () => {
      // console.log('* pageloaded');
      this.clearHighlightsAnnotations();
      this.fetchDocumentHighlightsAnnotations();
      this.neutralizeBothTools();
      /**
       * Fires once when the document’s first page is ready (unlike `pagechanged`, which runs on every page).
       * Align unless suppressed: footer/activity Next, or footer Previous with `navigateWithinActivity` off.
       */
      const suppressAlignFromNext = this.consumeSuppressScheduleAlignOnDocReaderPageload();
      const suppressAlignFromFooterPrev =
        this.consumeSuppressScheduleAlignOnDocReaderPageloadFromFooterPrevious();
      const slideId = lessonManager.currentLessonElementId;
      if (slideId && !suppressAlignFromNext && !suppressAlignFromFooterPrev) {
        lessonManager.scheduleAlignDocReaderToCurrentPdfChild(slideId);
      }
      setTimeout(() => {
        this.doPageChangedActions();
      }, 1000);
    });

    // Some things to do when the user has decided to navigate to a new page, but before it has been done.
    this.dcEvent_on(this._cwin, 'beforepagechanged', () => {
      this.setIsPageChanging(true);
      // console.log('beforepagechanged');
      // this.doSaveActions(true);
      this.clearPageAnnotation();
      this.clearPageHighlight();
    });

    // Do this right after the user has navigated to a different page.
    this.dcEvent_on(this._cwin, 'pagechanged', () => {
      this.setIsPageChanging(false);
      // console.log('pagechanged');
      this.doPageChangedActions();
    });

    // Do this right after the user has modified something.
    this.dcEvent_on(this._cwin, 'modcommandsent', (action, data) => {
      if (!this._isPageChanging) {
        if (action === 'svgtools.change' || action === 'highlighter.change') {
          this.doSaveActions(false);
        }
      }
    });
  };

  // _dcEvent
  dcEvent_store = {};

  dcEvent_on = (win, type, handler, context) => {
    const eventId = `dce_${Math.random().toString(36).substring(7)}`;
    // Create an object that holds information about this particular event and store it.
    this.dcEvent_store[eventId] = {
      type, // Event type
      handler, // Function that will be executed when event fires.
      context // The context will be the 'this' keyword in the handler function.
    };

    try {
      // Create the message event payload
      const obj = {
        type: 'dcevent',
        eventType: type.toString(),
        eventId // We need to provide the eventId so that we can identify this particular event registration when the event fires.
      };

      if (win && win.postMessage) {
        // console.log([obj]); // Log some info to the browser console, so that we can track the event.
        win.postMessage(obj, '*'); // Post the message. The asterisk should be replaced with the actual uri of the docReader iframe, if possible.
      }
    } catch (e) {
      console.error(e); // An error was caught, print some info about it to the console.
    }
  }

  dcEvent_fire =(eventId, args) => {
    const e = this.dcEvent_store[eventId];
    // Make sure there is handler function... if so, fire it.
    if (e && typeof e.handler === 'function') {
      // console.log(['dcEvent', e.type, eventId, args]); // Log some info to the browser console.
      e.handler.apply(e.context || window, args);
    } else {
      // Either we could not find the stored event object, or the handler function was in fact not a function. Throw a warning message in any case...
      console.warn('dcEvent', 'Handler is not a function. I\'m confused.');
    }
  }

  // _dcData
    dcData_store = {};

    dcData_get = (win, data, callback, context) => {
      const dataId = `dcd_${Math.random().toString(36).substring(7)}`;
      // Store some information about this message locally.
      this.dcData_store[dataId] = {
        data, // The data object that was sent to docReader
        callback, // The callback function
        context // The context will be the 'this' keyword in the callback function.
      };

      // Log some information about this object, so that we can more easily track it.
      // console.log(['dcData_get : dcData sent:', dataId, data]);

      if(win && win.postMessage) {
        win.postMessage({
          type: 'dcdata',
          dataId,
          dataObj: data
        }, '*');
      }
    }

    dcData_receive = (dataId, data) => {
      const d = this.dcData_store[dataId];
      // We want to see what we got back from docReader, so we log it to the browser console.
      // console.log([' dcData_receive : dcData returned: ', dataId, data]);

      // If the callback is a function, execute it. Data messages don't need to have a callback, since they sometimes only want to run a command in docReader.
      if (d && typeof d.callback === 'function') {
        // Callback was found, and it is a function, let's execute it.
        d.callback.apply(d.context || window, [data]);
      } else {
        // Uh oh, we could not execute the callback, let the world know!
        console.warn('Could not execute callback for dataId ', dataId);
      }
    }

    fetchDocumentHighlightsAnnotations = async () => {
      // const documentSessionId = '1234567890'; // await ContentService.getContentSessionId();
      const url = `${Auth.ecms}/api/getDocumentHighlightAnnotationPages?documentSessionId=${this.documentSessionId}`;
      try {
        const response = await Auth.fetch(url, {
          method: 'GET'
        });

        if (response.status === 'SUCCESS') {
          const pageAnnotations = response.data;
          this.loadPageAnnotations(pageAnnotations);
        }
      } catch (e) {
        console.warn(e);
      }
    }

    addUpdateDocumentHighlightAnnotationPage = async (
      courseResourceElementId, pageIndex, annotation, highlight, clear
    ) => {
      try {
        const body = {
          documentSessionId: this.documentSessionId,
          courseResourceElementId,
          pageIndex,
          annotation,
          highlight,
          userId: userManager.userId
        };

        const response = await Auth.fetch(`${Auth.ecms}/api/addUpdateDocumentHighlightAnnotationPage`, {
          method: 'POST',
          body
        });

        if (response.status === 'SUCCESS') {
          this.setPageAnnotation(pageIndex, annotation);
          this.setPageHighlight(pageIndex, highlight);
          if (clear) {
            this.clearPageAnnotation();
            this.clearPageHighlight();
          }
          return true;
        }
        return false;
      } catch (error) {
        alert('There was an error while saving your response.  Please remove any special characters such as emojis and submit your response again.');
        console.warn(error);
        return false;
      }
    }
}

// const thisManager = new DocReaderManager();
// export default thisManager;
export default new DocReaderManager();
